import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ObliqueIcon from '@/components/icons/ObliqueIcon';

export default function WorkflowsSettings() {
    const location = useLocation();
    const navigate = useNavigate();

    const isOverview = location.pathname.includes('/overview');
    const isWorkflows = location.pathname.includes('/workflows');

    // Default to overview if no specific tab is selected
    if (!isOverview && !isWorkflows) {
        return (
            <div className="animate-fade-in pb-16">
                <h2 className="text-2xl font-semibold text-[var(--text-primary)]">Automation</h2>
                <p className="text-[var(--text-tertiary)] mt-1">Select a tab above to manage your automations.</p>
            </div>
        );
    }

    if (isOverview) {
        return (
            <div className="animate-fade-in pb-16">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-semibold text-[var(--text-primary)] tracking-tight m-0">Automation</h2>
                    <div className="flex gap-3">
                        <button className="px-3 py-1.5 bg-transparent border border-[var(--border)] rounded-md text-[13px] font-semibold text-[var(--text-primary)] cursor-pointer flex items-center gap-1.5 hover:bg-[var(--bg-subtle)] transition-colors">
                            <ObliqueIcon name="warning" size={14} className="text-rose-600" /> Review automation issues
                        </button>
                        <button className="px-3 py-1.5 bg-transparent border border-[var(--border)] rounded-md text-[13px] font-semibold text-[var(--text-primary)] cursor-pointer hover:bg-[var(--bg-subtle)] transition-colors">
                            Edit Properties
                        </button>
                        <button className="px-3 py-1.5 bg-[var(--text-primary)] border-0 text-[var(--bg-surface)] rounded-md text-[13px] font-semibold cursor-pointer flex items-center gap-1 hover:opacity-90 transition-opacity">
                            Create workflow <ObliqueIcon name="arrow_drop_down" size={16} />
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
                    {/* What do you want to automate */}
                    <div className="p-6 bg-[var(--bg-surface)] rounded-xl border border-[var(--border)] shadow-xs">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-[15px] font-semibold text-[var(--text-primary)] m-0">What do you want to automate?</h3>
                            <span className="text-[10px] font-bold bg-pink-500 text-white px-1.5 py-0.5 rounded">AI</span>
                        </div>
                        <div className="relative">
                            <textarea 
                                placeholder="When an event happens, do an action"
                                className="w-full h-24 p-3 rounded-lg border border-pink-500/80 bg-transparent text-sm text-[var(--text-primary)] resize-none outline-none focus:ring-1 focus:ring-pink-500"
                            />
                            <button className="absolute bottom-3 right-3 bg-transparent border-0 text-pink-500 cursor-pointer hover:opacity-80">
                                <ObliqueIcon name="send" size={18} />
                            </button>
                        </div>
                    </div>

                    {/* Automation issues to review */}
                    <div className="p-6 bg-[var(--bg-surface)] rounded-xl border border-[var(--border)] flex flex-col items-center justify-center shadow-xs">
                        <h3 className="text-[15px] font-semibold text-[var(--text-primary)] mb-4 self-start">Automation issues to review</h3>
                        <div className="text-center">
                            <div className="text-xs font-bold text-[var(--text-tertiary)] tracking-wider">TOTAL</div>
                            <div className="text-3xl font-bold text-[var(--accent)] underline cursor-pointer">3</div>
                        </div>
                    </div>

                    {/* Pick up where you left off */}
                    <div className="p-6 bg-[var(--bg-surface)] rounded-xl border border-[var(--border)] shadow-xs">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-[15px] font-semibold text-[var(--text-primary)] m-0">Pick up where you left off</h3>
                            <span className="text-[13px] font-semibold text-[var(--accent)] cursor-pointer underline">View all</span>
                        </div>
                        <div className="flex items-center justify-center h-24 text-center text-[var(--text-secondary)] text-sm px-6">
                            Once you start building a workflow, it'll show up here for quick access.
                        </div>
                    </div>
                </div>

                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-[var(--text-primary)] m-0">Discover more automations</h3>
                    <span className="text-[13px] font-semibold text-[var(--accent)] cursor-pointer underline">View all templates</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
                    {[
                        { icon: 'attach_money', color: '#10b981', title: 'Automate deal pipelines' },
                        { icon: 'person', color: '#10b981', title: 'Automate lead pipelines' },
                        { icon: 'contact_mail', color: '#8b5cf6', title: 'Automate prospecting agent enrollment' },
                        { icon: 'account_tree', color: '#8b5cf6', title: 'Automate sequence enrollment' }
                    ].map((tpl, i) => (
                        <div key={i} className="p-5 bg-[var(--bg-surface)] rounded-xl border border-[var(--border)] flex flex-col h-44 shadow-xs">
                            <div className="flex justify-between mb-4">
                                <div 
                                    className="w-8 h-8 rounded-full flex items-center justify-center"
                                    style={{ border: `1px solid ${tpl.color}`, color: tpl.color }}
                                >
                                    <ObliqueIcon name={tpl.icon} size={18} />
                                </div>
                                <ObliqueIcon name="close" size={16} className="text-[var(--text-tertiary)] cursor-pointer hover:text-[var(--text-primary)]" />
                            </div>
                            <h4 className="text-[15px] font-semibold text-[var(--text-primary)] mb-4 flex-1">{tpl.title}</h4>
                            <button className="px-3 py-1 bg-transparent border border-[var(--border)] rounded-md text-[11px] font-semibold text-[var(--text-secondary)] w-fit hover:bg-[var(--bg-subtle)] transition-colors">
                                View automated setting
                            </button>
                        </div>
                    ))}
                </div>

                <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">Monitor automations</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <div className="p-6 bg-[var(--bg-surface)] rounded-xl border border-[var(--border)] shadow-xs">
                        <h4 className="text-sm font-semibold text-[var(--text-primary)] mb-4">Total active automations</h4>
                        <div className="flex justify-around text-center mt-6">
                            <div>
                                <div className="text-[11px] font-bold text-[var(--text-tertiary)] mb-2">WORKFLOWS</div>
                                <div className="text-2xl font-bold text-[var(--accent)]">29</div>
                            </div>
                            <div>
                                <div className="text-[11px] font-bold text-[var(--text-tertiary)] mb-2">SEQUENCES</div>
                                <div className="text-2xl font-bold text-[var(--accent)]">144</div>
                            </div>
                        </div>
                    </div>
                    <div className="p-6 bg-[var(--bg-surface)] rounded-xl border border-[var(--border)] shadow-xs">
                        <h4 className="text-sm font-semibold text-[var(--text-primary)] mb-2">Workflow enrollments</h4>
                        <div className="text-[11px] text-[var(--text-tertiary)] bg-[var(--bg-muted)] px-2 py-0.5 rounded inline-block">Date range: Last 6 months</div>
                    </div>
                    <div className="p-6 bg-[var(--bg-surface)] rounded-xl border border-[var(--border)] shadow-xs">
                        <h4 className="text-sm font-semibold text-[var(--text-primary)] mb-2">Most used workflow actions</h4>
                        <div className="text-[11px] text-[var(--text-tertiary)] bg-[var(--bg-muted)] px-2 py-0.5 rounded inline-block">Date range: All time</div>
                    </div>
                </div>
            </div>
        );
    }

    if (isWorkflows) {
        return (
            <div className="animate-fade-in pb-16">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-semibold text-[var(--text-primary)] tracking-tight m-0">Automation</h2>
                    <div className="flex gap-3">
                        <button className="px-3 py-1.5 bg-transparent border border-[var(--border)] rounded-md text-[13px] font-semibold text-[var(--text-primary)] cursor-pointer flex items-center gap-1.5 hover:bg-[var(--bg-subtle)] transition-colors">
                            <ObliqueIcon name="warning" size={14} className="text-rose-600" /> Review automation issues
                        </button>
                        <button className="px-3 py-1.5 bg-[var(--text-primary)] border-0 text-[var(--bg-surface)] rounded-md text-[13px] font-semibold cursor-pointer flex items-center gap-1 hover:opacity-90 transition-opacity">
                            Create workflow <ObliqueIcon name="arrow_drop_down" size={16} />
                        </button>
                    </div>
                </div>

                {/* Filters Row */}
                <div className="flex items-center gap-4 mb-4 border-b border-[var(--border)] pb-4 overflow-x-auto">
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-[var(--bg-subtle)] rounded-full text-[13px] font-semibold text-[var(--text-primary)]">
                        <div className="w-5 h-5 rounded-full bg-[var(--text-primary)] text-[var(--bg-surface)] flex items-center justify-center text-[11px]">69</div>
                        All workflows <ObliqueIcon name="close" size={14} className="cursor-pointer" />
                    </div>
                    <div className="text-[13px] font-medium text-[var(--text-tertiary)] cursor-pointer hover:text-[var(--text-primary)]">Deleted</div>
                    <div className="flex items-center gap-2 px-3 py-1.5 text-[13px] font-medium text-[var(--text-tertiary)] cursor-pointer hover:text-[var(--text-primary)]">
                        <div className="w-5 h-5 rounded-full bg-[var(--text-primary)] text-[var(--bg-surface)] flex items-center justify-center text-[11px]">39</div>
                        Unused
                    </div>
                    <div className="ml-auto flex items-center gap-4 text-[13px] font-semibold">
                        <span className="text-[var(--accent)] cursor-pointer flex items-center gap-1 hover:underline"><ObliqueIcon name="add" size={16} /> Add view (3/50)</span>
                        <span className="cursor-pointer text-[var(--text-secondary)] hover:text-[var(--text-primary)]">All views</span>
                        <button className="px-3 py-1.5 bg-transparent border border-[var(--border)] rounded-md flex items-center gap-1.5 text-[13px] font-semibold text-[var(--text-primary)] hover:bg-[var(--bg-subtle)] transition-colors">
                            <ObliqueIcon name="folder" size={16} /> Folders
                        </button>
                    </div>
                </div>

                <div className="flex items-center gap-4 mb-4 overflow-x-auto">
                    {['On or Off', 'Object Type', 'Created by', 'Folder', 'Created in', 'Action type'].map(filter => (
                        <div key={filter} className="flex items-center gap-1 text-[13px] font-semibold text-[var(--text-primary)] cursor-pointer hover:text-[var(--accent)]">
                            {filter} <ObliqueIcon name="arrow_drop_down" size={16} className="text-[var(--text-tertiary)]" />
                        </div>
                    ))}
                    <div className="text-[var(--text-tertiary)] flex items-center gap-1 text-[13px] font-semibold cursor-pointer hover:text-[var(--text-primary)]">
                        <ObliqueIcon name="add" size={16} /> Add quick filter
                    </div>
                    <div className="ml-auto flex items-center gap-4">
                        <div className="flex items-center gap-1 text-[13px] font-semibold text-[var(--text-primary)] cursor-pointer hover:text-[var(--accent)]">
                            <ObliqueIcon name="filter_list" size={18} /> Advanced filters
                        </div>
                    </div>
                </div>

                <div className="flex justify-between items-center mb-4">
                    <div className="relative w-72">
                        <ObliqueIcon name="search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" />
                        <input 
                            type="text" 
                            placeholder="Search workflows" 
                            className="w-full pl-9 pr-3 py-2 rounded-md border border-[var(--border)] bg-[var(--bg-surface)] text-[13px] text-[var(--text-primary)] outline-none focus:border-[var(--accent)]"
                        />
                    </div>
                    <button className="px-3 py-1.5 bg-transparent border border-[var(--border)] rounded-md text-[13px] font-semibold text-[var(--text-primary)] flex items-center gap-1 hover:bg-[var(--bg-subtle)] transition-colors">
                        Actions <ObliqueIcon name="arrow_drop_down" size={16} />
                    </button>
                </div>

                <div className="border border-[var(--border)] rounded-md overflow-hidden bg-[var(--bg-surface)] shadow-xs">
                    <table className="w-full border-collapse text-left">
                        <thead className="bg-[var(--bg-subtle)] border-b border-[var(--border)]">
                            <tr>
                                <th className="p-3 w-10"><input type="checkbox" className="accent-[var(--accent)]" /></th>
                                <th className="py-3 px-4 text-xs font-semibold text-[var(--text-primary)]">Name</th>
                                <th className="py-3 px-4 text-xs font-semibold text-[var(--text-primary)]">On or Off ⓘ</th>
                                <th className="py-3 px-4 text-xs font-semibold text-[var(--text-primary)]">Description</th>
                                <th className="py-3 px-4 text-xs font-semibold text-[var(--text-primary)]">Object type ⓘ</th>
                                <th className="py-3 px-4 text-xs font-semibold text-[var(--text-primary)]">Trigger Type</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[
                                { name: 'Deals Closed Won- Company Owner Sana Alam', on: true, desc: 'Updates the company owner to IK 6...', type: 'Deal', trigger: 'Filter criteria' },
                                { name: 'Contact Lead Status to Company Lead Status', on: true, desc: 'Updates the lead status when the lea...', type: 'Contact', trigger: 'Events' },
                                { name: 'Copy Website URL Property from contact to deal', on: false, desc: 'Copies the contact\'s website URL to ...', type: 'Deal', trigger: 'Filter criteria' },
                                { name: 'Update Amount property when Total In Bank Amo', on: true, desc: 'Updates the "Amount" property to m...', type: 'Deal', trigger: 'Events' },
                                { name: 'Update Associated Company Lead Source when mi', on: true, desc: 'Sets the "Associated Company Reco...', type: 'Deal', trigger: 'Filter criteria' },
                                { name: 'Update Associated Company Record ID when missi', on: true, desc: 'Sets the "Associated Company Reco...', type: 'Deal', trigger: 'Filter criteria' },
                                { name: 'Associate Company with Deals via Contacts', on: false, desc: '--', type: 'Deal', trigger: 'Filter criteria' },
                                { name: 'test', on: false, desc: '--', type: 'Contact', trigger: 'Events' },
                            ].map((row, i) => (
                                <tr key={i} className="border-b border-[var(--border)] hover:bg-[var(--bg-subtle)]/50 transition-colors">
                                    <td className="p-3"><input type="checkbox" className="accent-[var(--accent)]" /></td>
                                    <td className="py-3 px-4 text-[13px] font-semibold text-[var(--accent)] underline cursor-pointer">{row.name}</td>
                                    <td className="py-3 px-4 text-[13px]">
                                        <div className="flex items-center gap-1.5">
                                            <div className={`w-2 h-2 rounded-full ${row.on ? 'bg-[var(--success)]' : 'bg-[var(--text-tertiary)]'}`} />
                                            <span className="text-[var(--text-primary)]">{row.on ? 'On' : 'Off'}</span>
                                        </div>
                                    </td>
                                    <td className="py-3 px-4 text-[13px] text-[var(--text-secondary)]">{row.desc}</td>
                                    <td className="py-3 px-4 text-[13px] text-[var(--text-primary)]">{row.type}</td>
                                    <td className="py-3 px-4 text-[13px] text-[var(--text-secondary)]">{row.trigger}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="p-3 flex justify-center items-center gap-3 text-xs font-semibold text-[var(--text-primary)]">
                        <span className="text-[var(--text-tertiary)] cursor-pointer">&lt; Prev</span>
                        <div className="px-2 py-1 bg-[var(--bg-muted)] rounded">1</div>
                        <div className="cursor-pointer hover:text-[var(--accent)]">2</div>
                        <div className="cursor-pointer hover:text-[var(--accent)]">3</div>
                        <span className="cursor-pointer hover:text-[var(--accent)]">Next &gt;</span>
                        <span className="flex items-center gap-1 ml-3 text-[var(--text-secondary)] cursor-pointer">25 per page <ObliqueIcon name="arrow_drop_down" size={16} /></span>
                    </div>
                </div>
            </div>
        );
    }

    return null;
}
